import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { setBreadcrumbs } from '../../Core/Redux/Breadcrumbs'
import { selectCurrentUser } from '../../Auth/Redux/Auth'
import ConfirmationDialog from '../Components/ConfirmationDialog'
import config from '../../Core/Config'
import * as R from 'ramda'
import history from '../../Core/history'
import Logger from './Logger'
import Config from '../../Core/Config'
import { readFromStorage, storeInStorage } from './Storage'
import { isNotNilOrEmpty } from './Functional'
import {
  getDefaultListPerPage,
  getDefaultPageNumber,
  getDefaultSortDirection,
  getDefaultSortField,
} from '../Components/ChangeListTable/Rtk'

export const useCurrentUser = () => {
  return useSelector(selectCurrentUser)
}

export const useBreadcrumbs = (breadcrumbs, cond = true, ...deps) => {
  const dependencies = deps || []
  const dispatch = useDispatch()
  React.useEffect(() => {
    dispatch(setBreadcrumbs(cond ? breadcrumbs : []))
  }, [cond, JSON.stringify(breadcrumbs), ...dependencies]) // component did mount
}

export const useConfirm = (onConfirm, onCancel) => {
  const [state, setState] = React.useState({
    open: false,
    spreadPayload: false,
    payload: null,
    maxWidth: 'sm',
  })
  const open = React.useCallback(
    (payload, title, content, options, spreadPayload, maxWidth = 'sm') => {
      if (options?.selected) {
        content = (
          <>
            {content}
            <ul>
              {options.selected.map((item, idx) => {
                let val
                if (R.is(Function, options.field)) {
                  val = options.field(item)
                } else {
                  val = options.field
                    .split('.')
                    .reduce((acc, curr) => (acc ? acc[curr] : null), item)
                }
                return <li key={idx}>{val}</li>
              })}
            </ul>
          </>
        )
      }
      setState({ open: true, title, content, payload, spreadPayload, maxWidth })
    },
    [setState],
  )
  const close = () => {
    setState({ open: false })
  }
  const handleConfirm = React.useCallback(() => {
    close()
    if (state.spreadPayload) {
      return onConfirm(...state.payload)
    } else {
      return onConfirm(state.payload)
    }
  }, [onConfirm, state.payload])

  const confirm = (
    <ConfirmationDialog
      open={state.open}
      title={state.title}
      onCancel={() => {
        onCancel && onCancel()
        setState({ open: false })
      }}
      onOk={handleConfirm}
      maxWidth={state.maxWidth}
    >
      {state.content}
    </ConfirmationDialog>
  )

  return [open, confirm, close]
}

export const useForm = (defaultFields, onSetField) => {
  const [fields, setFields] = React.useState(defaultFields)
  const [errors, setErrors] = React.useState({})
  const clearErrors = () => setErrors({})
  const setField =
    (field, type, dft = null) =>
    (value) => {
      switch (type) {
        case 'int':
          value = R.either(R.isEmpty, R.isNil)(value) ? dft : parseInt(value)
          break
        case 'float':
          value = R.either(R.isEmpty, R.isNil)(value) ? dft : parseFloat(value)
          break
        case 'array':
          value = R.isNil(value) ? dft : value
          break
        default:
          value = R.isEmpty(value) ? dft : value
      }

      setFields({ ...fields, [field]: value })
      onSetField && onSetField(field, value)
    }

  return { fields, setField, setFields, errors, setErrors, clearErrors }
}

export const useDebounce = (value, delay = config.debounceTime) => {
  const [debouncedValue, setDebouncedValue] = React.useState(value)

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

export const useIsSaving = (handleSubmit) => {
  const [isSaving, setIsSaving] = React.useState(false)

  return {
    isSaving,
    handleSubmit: async (...args) => {
      setIsSaving(true)
      const res = await handleSubmit(...args)
      setIsSaving(false)
      return res
    },
  }
}

/**
 * Use page not found hook
 * Utility to redirect the user to a 404 page if condition is met
 *
 * @param {Boolean} condition - condition to watch for redirect
 * @param {Array} deps - list of deps to check for change (useEffect)
 */
export const usePageNotFoundRedirect = (condition, deps) => {
  React.useEffect(() => {
    if (condition) history.push(config.urls.pageNotFound)
  }, deps)
}

export const useForceUpdate = () => {
  const [value, setValue] = React.useState(0)
  Logger.debug('useForceUpdate', value)
  return () => setValue((value) => value + 1)
}

export const useFiltersForm = (defaultFields, storageKey, Form, formProps={}) => {
  const {
    fields: filterFields,
    setField: setFilterField,
    setFields: setFilterFields,
  } = useForm(
    Config.enableSaveFilters
      ? readFromStorage(storageKey, defaultFields)
      : defaultFields,
  )
  const debouncedFilterFields = useDebounce(filterFields)
  const handleFilterForm = React.useCallback(
    (handleClose) => (
      <Form
        onClose={handleClose}
        fields={filterFields}
        setField={setFilterField}
        setFields={setFilterFields}
        onReset={() => {
          setFilterFields(defaultFields)
          handleClose()
        }}
        onSave={() => storeInStorage(storageKey, filterFields)}
        {...formProps}
      />
    ),
    [Form, filterFields, setFilterField],
  )

  const filterFormActive = R.values(filterFields).some(isNotNilOrEmpty)

  return {
    filterFields,
    setFilterField,
    setFilterFields,
    debouncedFilterFields,
    handleFilterForm,
    filterFormActive,
  }
}

// importing from API causes error
const apiList = (res) => ({
  ...res,
  orData: res.data,
  data: res.data?.data?.value || [],
})

export const useRtkChangeListQuery = (ctName, query, qsAdditions, opts, queryOpts={}) => {
  const dftSortField = getDefaultSortField(
    ctName,
    R.defaultTo('id', opts?.defaultSortField),
  )
  const dftSortDirection = getDefaultSortDirection(
    ctName,
    R.defaultTo('asc', opts?.defaultSortDirection),
  )

  const [qs, setQs] = React.useState({
    base: {
      top: getDefaultListPerPage(ctName, opts?.defaultListPerPage),
      skip: getDefaultPageNumber(ctName) * getDefaultListPerPage(ctName, opts?.defaultListPerPage),
      orderby: dftSortField,
      ordertype: dftSortDirection,
    },
    qsAdditions,
  })

  const refreshData = setQs
  const { data, orData, isFetching, refetch } = apiList(
    query({ ...qs.base, ...qs.qsAdditions }, queryOpts)
  )

  const dataCount = orData?.data['@odata.count'] || -1

  return {
    qs,
    isFetching,
    data,
    orData,
    dataCount,
    refetch,
    refreshData,
    dftSortField,
    dftSortDirection,
  }
}
